import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaHeart, FaTrash, FaShoppingCart, FaEye, FaHandshake, FaCheckCircle, FaComments } from 'react-icons/fa';
import './Wishlist.css';

const Wishlist = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interestedProducts, setInterestedProducts] = useState(new Set());

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      const response = await api.get('/wishlist');
      const products = response.data.wishlist || [];
      setWishlist(products);
      
      // Check which products user has shown interest in
      const interested = new Set();
      products.forEach(product => {
        if (product.interests && product.interests.some(interest => interest.buyer === user._id)) {
          interested.add(product._id);
        }
      });
      setInterestedProducts(interested);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleInterest = async (productId) => {
    try {
      await api.post(`/products/${productId}/interest`);
      setInterestedProducts(prev => new Set([...prev, productId]));
      toast.success('Interest registered! Seller will contact you.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to register interest');
    }
  };

  const handleStartChat = async (product) => {
    try {
      const response = await api.post('/chat/start', {
        recipientId: product.seller._id,
        productId: product._id,
        message: `Hi, I'm interested in "${product.title}" from my wishlist`
      });
      toast.success('Chat started!');
      navigate(`/chat/${response.data.chat._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start chat');
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      await api.delete(`/wishlist/${productId}`);
      setWishlist(prev => prev.filter(item => item._id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
      toast.error('Failed to remove from wishlist');
    }
  };

  const clearWishlist = async () => {
    if (!window.confirm('Are you sure you want to clear your entire wishlist?')) {
      return;
    }

    try {
      await api.post('/wishlist/clear');
      setWishlist([]);
      toast.success('Wishlist cleared');
    } catch (error) {
      console.error('Failed to clear wishlist:', error);
      toast.error('Failed to clear wishlist');
    }
  };

  const viewProduct = (productId) => {
    navigate(`/products/${productId}`);
  };

  const createOrder = (product) => {
    navigate(`/products/${product._id}`);
  };

  const calculateFinalPrice = (product) => {
    const { basePrice, gst, discountPercent } = product.pricing;
    const discountedPrice = basePrice - (basePrice * (discountPercent / 100));
    const finalPrice = discountedPrice + (discountedPrice * (gst / 100));
    return finalPrice;
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading wishlist...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="wishlist-page">
        <div className="page-header">
          <div>
            <h1>
              <FaHeart style={{ color: '#ef4444', marginRight: '12px' }} />
              My Wishlist
            </h1>
            <p>{wishlist.length} {wishlist.length === 1 ? 'item' : 'items'} saved</p>
          </div>
          {wishlist.length > 0 && (
            <button className="btn btn-outline btn-danger" onClick={clearWishlist}>
              Clear All
            </button>
          )}
        </div>

        {wishlist.length === 0 ? (
          <div className="empty-wishlist">
            <FaHeart style={{ fontSize: '64px', color: '#d1d5db', marginBottom: '16px' }} />
            <h2>Your wishlist is empty</h2>
            <p>Save products you like to buy them later</p>
            <button className="btn btn-primary" onClick={() => navigate('/products')}>
              Browse Products
            </button>
          </div>
        ) : (
          <div className="wishlist-grid">
            {wishlist.map((product) => (
              <div key={product._id} className="wishlist-item">
                <div className="item-image" onClick={() => viewProduct(product._id)}>
                  {product.images && product.images.length > 0 ? (
                    <img src={product.images[0].url} alt={product.title} />
                  ) : (
                    <div className="no-image">No Image</div>
                  )}
                  {product.pricing.discountPercent > 0 && (
                    <span className="discount-badge">-{product.pricing.discountPercent}%</span>
                  )}
                  <button
                    className="remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromWishlist(product._id);
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>

                <div className="item-details">
                  <div className="item-header">
                    <span className="category-tag">{product.category}</span>
                    {product.verified && (
                      <span className="verified-badge">✓ Verified</span>
                    )}
                  </div>

                  <h3 onClick={() => viewProduct(product._id)}>{product.title}</h3>
                  
                  <div className="seller-info">
                    <span>by {product.seller?.businessName || 'Unknown Seller'}</span>
                  </div>

                  <div className="item-pricing">
                    <div className="price-row">
                      {product.pricing.discountPercent > 0 && (
                        <span className="original-price">
                          ₹{product.pricing.basePrice.toFixed(2)}
                        </span>
                      )}
                      <span className="final-price">
                        ₹{calculateFinalPrice(product).toFixed(2)}
                      </span>
                    </div>
                    <span className="price-note">+ GST {product.pricing.gst}%</span>
                  </div>

                  <div className="item-stock">
                    {product.inventory.available ? (
                      <span className="in-stock">✓ In Stock</span>
                    ) : (
                      <span className="out-stock">Out of Stock</span>
                    )}
                    {product.inventory.quantity && (
                      <span className="quantity">{product.inventory.quantity} units</span>
                    )}
                  </div>

                  {/* Interest Status */}
                  {interestedProducts.has(product._id) && (
                    <div className="interest-badge">
                      <FaCheckCircle style={{ color: '#10b981' }} />
                      <span>Interest Registered</span>
                    </div>
                  )}

                  <div className="item-actions">
                    <button
                      className="btn btn-primary btn-block"
                      onClick={() => createOrder(product)}
                      disabled={!product.inventory.available}
                    >
                      <FaShoppingCart /> Order Now
                    </button>
                    
                    <div className="action-row">
                      {!interestedProducts.has(product._id) ? (
                        <button
                          className="btn btn-success"
                          onClick={() => handleInterest(product._id)}
                        >
                          <FaHandshake /> Show Interest
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline"
                          onClick={() => handleStartChat(product)}
                        >
                          <FaComments /> Chat with Seller
                        </button>
                      )}
                      
                      <button
                        className="btn btn-outline"
                        onClick={() => viewProduct(product._id)}
                      >
                        <FaEye /> View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Wishlist;
